import useEventListener from './use-event-listener'
import { DragAndDropListenerMapping } from '../drag-n-drop-provider'

/**
 * **useDragNDropListeners** is as a React hook that helps with the addition of event listeners
 *   for Drag & Drop related events on an html element
 *
 * @param eventTarget
 *  Typically an HTMLElement, but can be null to help obey the Rules of Hooks
 *     When null, no listeners will actually be added since there is no target to add them
 * @param eventListeners
 *  An object mapping optionally event listeners (or null/undefined) for each of the potential
 *  Drag & Drop related event types:
 *
 *  ### Drag & Drop event types
 *  #### drag
 *  An element or text selection is being dragged (fired continuously every 350ms).
 *  #### dragend
 *  A drag operation is being ended (by releasing a mouse button or hitting the escape key)
 *  #### dragenter
 *  A dragged element or text selection enters a valid drop target.
 *  #### dragstart
 *  The user starts dragging an element or text selection.
 *  #### dragleave
 *  A dragged element or text selection leaves a valid drop target.
 *  #### dragover
 *  An element or text selection is being dragged over a valid drop target (fired continuously every 350ms).
 *  #### drop
 *  An element is dropped on a valid drop target.
 */
const useDragNDropListeners = (
  eventTarget: HTMLElement | null,
  eventListeners: DragAndDropListenerMapping,
) => {
  useEventListener('dragend', eventListeners['dragend'] ?? null, eventTarget)
  useEventListener(
    'dragenter',
    eventListeners['dragenter'] ?? null,
    eventTarget,
  )
  useEventListener(
    'dragstart',
    eventListeners['dragstart'] ?? null,
    eventTarget,
  )
  useEventListener(
    'dragleave',
    eventListeners['dragleave'] ?? null,
    eventTarget,
  )
  useEventListener('dragover', eventListeners['dragover'] ?? null, eventTarget)
  useEventListener('drop', eventListeners['drop'] ?? null, eventTarget)
}

export default useDragNDropListeners
